import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import {
  validateDateOfBirth,
  validateDateThatIsAfter,
} from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { makeAutoObservable, runInAction, reaction } from 'mobx';
import { createContext, useContext } from 'react';
import { inviteEmployeesToGroup } from 'api/operations/userService';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ApolloError } from '@apollo/client/errors';
import { ServerError } from '@apollo/client';
import { bpHistory } from 'global/bpHistory';
import { SubscriberAddOnMethod } from 'types/electionService';

interface AddEmployeeViaFormFields extends DefaultFormFields {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  dob: FormControl<string>;
  invite: FormControl<string>;
}

interface AddEmployeeViaFormPageStoreParams {
  groupId: string;
}

export class AddEmployeeViaFormPageStore {
  private _isLoading = false;
  private _groupId: string;
  private _isUserInTheSystem = false;

  public formController: FormController<AddEmployeeViaFormFields> = new FormController({
    firstName: new FormControl<string>('', { validations: ['printableCharacters'], optional: false, trim: true }),
    lastName: new FormControl<string>('', { validations: ['printableCharacters'], optional: false, trim: true }),
    email: new FormControl('', { validations: ['isEmailValid'], optional: false }),
    dob: new FormControl<string>('', {
      optional: false,
      customValidations: [
        {
          check: (value: string): boolean => validateDateOfBirth(value, 120),
          msg: 'Incorrect date picked',
        },
        {
          check: (value: string): boolean => !validateDateThatIsAfter(value, `${new Date().getFullYear() - 13}-01-01`), // validation checks that EE is older than 14
          msg: 'Employee must be at least 14 years old to invite',
        },
      ],
    }),
    invite: new FormControl<string>('false', { optional: false }),
  });

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get isFormValid(): boolean {
    return this.formController.basicFieldsValidationsPassed;
  }

  public get isUserInTheSystem(): boolean {
    return this._isUserInTheSystem;
  }

  private _setLoading(value: boolean): void {
    this._isLoading = value;
  }

  private _setIsUserInTheSystem(value: boolean): void {
    this._isUserInTheSystem = value;
  }

  private async _inviteEmployeeToGroup(): Promise<void> {
    this._setLoading(true);

    await runInAction(async (): Promise<void> => {
      if (!this.isFormValid) {
        throw new Error('Attempted to submit invalid form!');
      }
      try {
        await inviteEmployeesToGroup(
          this._groupId,
          [this.formController.getFormDataObject() as Record<string, string>],
          SubscriberAddOnMethod.FORM,
          false,
          this.formController.fields.invite.value === 'true',
        );
        runInAction(() => {
          this._setLoading(false);
          toastManager.addToast(
            'Invitation sent successfully! The employee will receive an email with their registration link.',
            ToastColorTypes.SUCCESS,
          );
        });
      } catch (error) {
        const networkError = (error as ApolloError)?.networkError as ServerError;
        runInAction(() => {
          this._setLoading(false);
          if (networkError?.statusCode === 409) {
            this._setIsUserInTheSystem(true);
          } else {
            toastManager.addToast('Something went wrong during invite', ToastColorTypes.WARNING);
          }
        });
      }
    });
  }

  // Keeping this logic here to avoid repeating it across page content components
  // eslint-disable-next-line class-methods-use-this
  public returnBack(): void {
    bpHistory.goBack();
  }

  public async saveAndReturnToList(): Promise<void> {
    await this._inviteEmployeeToGroup();
    runInAction(() => {
      if (!this.isUserInTheSystem) {
        this.returnBack();
      }
    });
  }

  public async saveAndAddNewEmployee(): Promise<void> {
    await this._inviteEmployeeToGroup();
    runInAction(() => {
      if (!this.isUserInTheSystem) {
        this.formController.resetForm();
      }
    });
  }

  constructor({ groupId }: AddEmployeeViaFormPageStoreParams) {
    this._groupId = groupId;

    // Reset error state when email changes
    reaction(
      () => this.formController.fields.email.value,
      () => {
        if (this.formController.fields.email.value) {
          this._setIsUserInTheSystem(false);
        }
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const AddEmployeeViaFormPageStoreContext = createContext(new AddEmployeeViaFormPageStore({ groupId: 'mock-group-id' }));

export const useAddEmployeeViaFormPageStore = (): AddEmployeeViaFormPageStore => {
  return useContext(AddEmployeeViaFormPageStoreContext);
};

export { AddEmployeeViaFormPageStoreContext };
